import type { ButtonBaseProps } from '@mui/material/ButtonBase';

import { useCallback } from 'react';

import Box from '@mui/material/Box';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';

import { setStorage } from 'src/hooks/use-local-storage';

import { Iconify } from 'src/components/iconify';
import { usePopover, CustomPopover } from 'src/components/custom-popover';
import { useWorkspaceContext } from 'src/components/workspace/use-workspace-context';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export type WorkspacesPopoverProps = ButtonBaseProps & {
  data?: {
    guid: string;
    name: string;
  }[];
};

export function WorkspacesPopover({ sx, ...other }: WorkspacesPopoverProps) {
  const popover = usePopover();

  const mediaQuery = 'sm';

  const { orgGuid, setOrgGuid } = useWorkspaceContext();
  const { employerOrgs } = useAuthContext();

  const handleChangeWorkspace = useCallback(
    (guid: string) => {
      setOrgGuid(guid);
      popover.onClose();
      setStorage('place-list-map-bounds', null);
    },
    [popover, setOrgGuid]
  );

  if (!employerOrgs) {
    return null;
  }

  const currentOrg = employerOrgs.find((item) => item.guid === orgGuid);

  return (
    <>
      <ButtonBase
        disableRipple
        disabled={employerOrgs.length <= 1}
        onClick={popover.onOpen}
        sx={{
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
          ...sx,
        }}
        {...other}
      >
        <Box
          component="span"
          sx={{
            typography: 'subtitle2',
            display: { xs: 'none', [mediaQuery]: 'inline-flex' },
          }}
        >
          {currentOrg?.name}
        </Box>

        {employerOrgs.length > 1 && (
          <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
        )}
      </ButtonBase>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{ arrow: { placement: 'top-left' } }}
      >
        <MenuList sx={{ width: 240 }}>
          {employerOrgs.map((option: { guid: string; name: string }) => (
            <MenuItem
              key={option.guid}
              selected={option.guid === orgGuid}
              onClick={() => handleChangeWorkspace(option.guid)}
              sx={{ height: 48 }}
            >
              <Box component="span" sx={{ flexGrow: 1 }}>
                {option.name}
              </Box>
            </MenuItem>
          ))}
        </MenuList>
      </CustomPopover>
    </>
  );
}
